import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import tippy from 'tippy.js';
import { EmojiItem, searchEmojis } from '../../emojiPicker/EmojiPicker';

const EmojiSuggestionList = forwardRef((props: any, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [props.items]);

  const selectItem = (index: number) => {
    const item = props.items[index] as EmojiItem | undefined;
    if (item) props.command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (!props.items.length) return false;
      if (event.key === 'ArrowUp') {
        setSelectedIndex((current) => (current + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((current) => (current + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="chat-emoji-suggestion grid max-h-64 w-72 grid-cols-2 gap-1 overflow-y-auto rounded-xl bg-white p-1.5 shadow-xl">
      {props.items.length ? props.items.map((item: EmojiItem, index: number) => (
        <button
          key={item.emoji}
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => selectItem(index)}
          className={`chat-emoji-suggestion-option flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition ${index === selectedIndex ? 'bg-purple-50 text-[#6847A3]' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          <span className="text-xl" aria-hidden="true">{item.emoji}</span>
          <span className="truncate text-xs font-medium">:{item.keywords[0]}:</span>
        </button>
      )) : (
        <div className="col-span-2 px-3 py-3 text-center text-xs italic text-slate-400">No emojis found</div>
      )}
    </div>
  );
});

EmojiSuggestionList.displayName = 'EmojiSuggestionList';

export const EmojiSuggestion = Extension.create({
  name: 'emojiSuggestion',

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: ':',
        allowSpaces: false,
        startOfLine: false,
        // Search the full shared catalog, then show a concise ranked window.
        // Typing additional shortcode characters exposes every catalog entry.
        items: ({ query }) => searchEmojis(query, 24),
        command: ({ editor, range, props }) => {
          editor.chain().focus().deleteRange(range).insertContent((props as EmojiItem).emoji).run();
        },
        render: () => {
          let component: ReactRenderer<any> | undefined;
          let popup: any[] | undefined;

          return {
            onStart: (props) => {
              component = new ReactRenderer(EmojiSuggestionList, { props, editor: props.editor });
              if (!props.clientRect) return;
              popup = tippy('body', {
                getReferenceClientRect: props.clientRect as any,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'top-start',
              });
            },
            onUpdate: (props) => {
              component?.updateProps(props);
              if (props.clientRect && popup?.[0]) {
                popup[0].setProps({ getReferenceClientRect: props.clientRect });
              }
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                props.event.preventDefault();
                props.event.stopPropagation();
                popup?.[0]?.hide();
                return true;
              }
              return (component?.ref as any)?.onKeyDown(props) ?? false;
            },
            onExit: () => {
              popup?.[0]?.destroy();
              component?.destroy();
            },
          };
        },
      }),
    ];
  },
});
